/**
 * paymentService.js
 *
 * Client-side payment helpers supporting two modes via `paymentType`:
 *   "one_time"    — classic Razorpay Order (single charge)
 *   "subscription"— Razorpay Subscription (recurring monthly billing)
 *
 * Functions:
 *   1. initiatePayment()      — calls backend POST /api/payment/initiate
 *   2. getPaymentStatus()     — polls backend for subscription_status
 *   3. verifyPayment()        — posts Razorpay callback fields for verification
 *   4. loadRazorpayScript()   — lazily loads the Razorpay Checkout SDK
 *   5. openRazorpayCheckout() — opens the Razorpay payment modal
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Initiate a payment — creates either a Razorpay Order (one_time) or
 * a Razorpay Subscription (subscription) on the backend.
 *
 * @param {string} authToken    - Firebase/JWT bearer token
 * @param {string} paymentType  - "one_time" | "subscription"
 * @param {object} [options]    - Extra options depending on paymentType:
 *   For "one_time":    { amountPaise: number, receipt?: string }
 *   For "subscription": { planId?: string, totalCount?: number }
 * @returns {Promise<InitiatePaymentResponse>}
 *   one_time:     { payment_type, key_id, order_id, amount, currency, receipt }
 *   subscription: { payment_type, key_id, subscription_id, plan_id }
 */
export async function initiatePayment(authToken, paymentType, options = {}) {
    const body = { payment_type: paymentType };

    if (paymentType === "one_time") {
        body.amount = options.amountPaise;
        body.currency = options.currency || "INR";
        if (options.receipt) body.receipt = options.receipt;
    } else {
        // subscription
        if (options.planId) body.plan_id = options.planId;
        if (options.totalCount) body.total_count = options.totalCount;
    }

    const response = await fetch(`${API_URL}/api/payment/initiate`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Payment initiation failed (${response.status})`);
    }

    return response.json();
}

/**
 * Poll the backend for the authenticated user's current subscription status.
 * Used while payment is in 'pending_capture' state to detect webhook confirmation.
 *
 * @param {string} authToken - Firebase/JWT bearer token
 * @returns {Promise<{ subscription_status: string | null }>}
 */
export async function getPaymentStatus(authToken) {
    const response = await fetch(`${API_URL}/api/payment/status`, {
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
        throw new Error(`Status check failed (${response.status})`);
    }
    return response.json();
}

/**
 * Cancel the authenticated user's active subscription.
 * Calls backend which cancels via Razorpay at the end of current cycle.
 *
 * @param {string} authToken - Firebase/JWT bearer token
 * @returns {Promise<{ status: string, message: string }>}
 */
export async function cancelSubscription(authToken) {
    const response = await fetch(`${API_URL}/api/payment/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Cancellation failed (${response.status})`);
    }
    return response.json();
}

/**
 * Fetch the free-trial status for the authenticated user.
 *
 * @param {string} authToken - Firebase/JWT bearer token
 * @returns {Promise<{ is_trial: boolean, trial_expired?: boolean, days_remaining?: number, trial_end_date?: string }>}
 */
export async function getTrialStatus(authToken) {
    const response = await fetch(`${API_URL}/api/payment/trial-status`, {
        headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
        throw new Error(`Trial status check failed (${response.status})`);
    }
    return response.json();
}

/**
 * Verify the payment with the backend after Razorpay Checkout fires its success handler.
 *
 * For "one_time":    POSTs { payment_type, razorpay_payment_id, razorpay_order_id, razorpay_signature }
 * For "subscription": POSTs { payment_type, razorpay_payment_id, razorpay_subscription_id, razorpay_signature }
 *
 * @param {string} authToken       - Firebase/JWT bearer token
 * @param {string} paymentType     - "one_time" | "subscription"
 * @param {object} razorpayResponse - Fields from Razorpay handler callback
 * @returns {Promise<VerifyPaymentResponse>}
 */
export async function verifyPayment(authToken, paymentType, razorpayResponse) {
    const body = {
        payment_type: paymentType,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
        // one_time fields
        ...(razorpayResponse.razorpay_order_id
            ? { razorpay_order_id: razorpayResponse.razorpay_order_id }
            : {}),
        // subscription fields
        ...(razorpayResponse.razorpay_subscription_id
            ? { razorpay_subscription_id: razorpayResponse.razorpay_subscription_id }
            : {}),
    };

    const response = await fetch(`${API_URL}/api/payment/verify`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || `Payment verification failed (${response.status})`);
    }

    return response.json();
}

/**
 * Lazily load the Razorpay Checkout script.
 * Safe to call multiple times — resolves immediately if already loaded.
 *
 * @returns {Promise<boolean>} true when the script is ready
 */
export function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

/**
 * Open the Razorpay Checkout modal.
 *
 * Automatically adapts to payment type:
 *   "one_time"    — uses order_id from paymentData; handler returns razorpay_order_id
 *   "subscription"— uses subscription_id from paymentData; handler returns razorpay_subscription_id
 *
 * @param {string}   paymentType  - "one_time" | "subscription"
 * @param {object}   paymentData  - Response from initiatePayment()
 * @param {object}   prefill      - { name, email, contact } for the checkout form
 * @param {function} onSuccess    - Called with the Razorpay response on success
 * @param {function} onFailure    - Called with an Error on failure or dismissal
 */
export function openRazorpayCheckout(paymentType, paymentData, prefill, onSuccess, onFailure) {
    if (!window.Razorpay) {
        onFailure(new Error("Razorpay SDK not loaded. Please refresh and try again."));
        return;
    }

    const options = {
        key: paymentData.key_id,
        name: "Nutri Saathi",
        description: paymentType === "subscription" ? "Monthly Subscription" : "Diet Coaching Plan",
        theme: { color: "#D48C70" },
        prefill: {
            name: prefill.name || "",
            email: prefill.email || "",
            contact: prefill.contact || "",
        },
        handler: (response) => onSuccess(response),
        modal: {
            ondismiss: () => onFailure(new Error("Payment was cancelled.")),
        },
    };

    if (paymentType === "subscription") {
        // Subscription flow: Razorpay Checkout uses subscription_id
        // handler response: { razorpay_payment_id, razorpay_subscription_id, razorpay_signature }
        options.subscription_id = paymentData.subscription_id;
    } else {
        // One-time flow: Razorpay Checkout uses order_id + amount + currency
        // handler response: { razorpay_payment_id, razorpay_order_id, razorpay_signature }
        options.order_id = paymentData.order_id;
        options.amount = paymentData.amount;   // already in paise
        options.currency = paymentData.currency;
    }

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (response) => {
        onFailure(new Error(response.error?.description || "Payment failed."));
    });
    rzp.open();
}
